import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router';
import { api } from '@/lib/api-client';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter,
} from '@/components/ui/sheet';

import { Key, Plus, Trash2, Copy, Check, AlertTriangle, Loader2, Package } from 'lucide-react';

interface SubscriptionDetail {
    id: string;
    status: string;
    application_id: string;
    application_name?: string;
    product_name?: string;
    subscriber_org_id: string;
    created_at: string;
}

interface SubscriberKeyItem {
    id: string;
    client_id: string;
    client_name: string;
    description?: string;
    created_at: string;
}

interface CreatedKeyCredentials {
    client_id: string;
    client_secret: string;
    client_name: string;
    description?: string;
    created_at: string;
}

export default function SubscriptionDetailPage() {
    const { name: id } = useParams<{ name: string }>();
    usePageTitle('Subscription');
    const { setHeaderTitle } = useHeaderAction();

    const [subscription, setSubscription] = useState<SubscriptionDetail | null>(null);
    const [keys, setKeys] = useState<SubscriberKeyItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [keysLoading, setKeysLoading] = useState(true);

    const [createOpen, setCreateOpen] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createDesc, setCreateDesc] = useState('');
    const [creating, setCreating] = useState(false);
    const [credentials, setCredentials] = useState<CreatedKeyCredentials | null>(null);
    const [copiedId, setCopiedId] = useState(false);
    const [copiedSecret, setCopiedSecret] = useState(false);

    const [revokeId, setRevokeId] = useState<string | null>(null);
    const [revoking, setRevoking] = useState(false);

    const fetchSubscription = useCallback(async () => {
        if (!id) return;
        try {
            const { data } = await api.get<SubscriptionDetail>(`/billing/subscriptions/${id}`);
            setSubscription(data);
        } catch {
            toast.error('Failed to load subscription');
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchKeys = useCallback(async () => {
        if (!id) return;
        try {
            const { data } = await api.get<{ api_keys: SubscriberKeyItem[] }>(`/subscriptions/${id}/keys`);
            setKeys(data.api_keys ?? []);
        } catch {
            setKeys([]);
        } finally {
            setKeysLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchSubscription();
        fetchKeys();
    }, [fetchSubscription, fetchKeys]);

    useEffect(() => {
        setHeaderTitle({
            icon: (
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100 text-violet-700">
                    <Package size={16} />
                </div>
            ),
            title: subscription?.product_name ?? 'Subscription',
            subtitle: subscription ? `Status: ${subscription.status}` : undefined,
        });
    }, [subscription, setHeaderTitle]);

    const handleCreate = async () => {
        if (!id || !createName.trim()) return;
        setCreating(true);
        try {
            const { data } = await api.post<CreatedKeyCredentials>(`/subscriptions/${id}/keys`, {
                display_name: createName.trim(),
                description: createDesc.trim() || undefined,
            });
            setCredentials(data);
        } catch (err: unknown) {
            const msg =
                (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
                'Failed to create key';
            toast.error(msg);
        } finally {
            setCreating(false);
        }
    };

    const handleRevoke = async () => {
        if (!id || !revokeId) return;
        setRevoking(true);
        try {
            await api.delete(`/subscriptions/${id}/keys/${revokeId}`);
            toast.success('Key revoked');
            setRevokeId(null);
            fetchKeys();
        } catch {
            toast.error('Failed to revoke key');
        } finally {
            setRevoking(false);
        }
    };

    const copyToClipboard = async (text: string, type: 'id' | 'secret') => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === 'id') {
                setCopiedId(true);
                setTimeout(() => setCopiedId(false), 2000);
            } else {
                setCopiedSecret(true);
                setTimeout(() => setCopiedSecret(false), 2000);
            }
        } catch { /* ignore */ }
    };

    const closeCreateSheet = () => {
        if (credentials) {
            fetchKeys();
        }
        setCreateOpen(false);
        setCreateName('');
        setCreateDesc('');
        setCredentials(null);
        setCopiedId(false);
        setCopiedSecret(false);
    };

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!subscription) {
        return <div className="p-8 text-center text-slate-500">Subscription not found.</div>;
    }

    const isActive = subscription.status === 'active' || subscription.status === 'trialing';

    return (
        <div className="mx-auto max-w-4xl space-y-8 p-6">
            {/* Subscription header */}
            <div className="flex items-center justify-between rounded-lg border bg-white p-5">
                <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                        {subscription.product_name ?? subscription.application_name ?? 'Subscription'}
                    </h2>
                    <p className="text-sm text-slate-500">ID: {subscription.id}</p>
                </div>
                <Badge variant={isActive ? 'default' : 'secondary'}>
                    {subscription.status}
                </Badge>
            </div>

            {/* API Keys section */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-base font-semibold text-slate-900">API Keys</h3>
                        <p className="text-sm text-slate-500">
                            Credentials for calling capabilities of this application via client_credentials grant.
                        </p>
                    </div>
                    <Button
                        onClick={() => setCreateOpen(true)}
                        disabled={!isActive}
                        size="sm"
                        className="gap-1.5"
                    >
                        <Plus size={14} />
                        Create Key
                    </Button>
                </div>

                {!isActive && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
                        <p className="text-sm text-amber-800">
                            Subscription is not active. New keys cannot be created and existing keys may not work.
                        </p>
                    </div>
                )}

                {keysLoading ? (
                    <div className="flex h-32 items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                ) : keys.length === 0 ? (
                    <div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
                        No API keys yet. Create one to call capabilities programmatically.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {keys.map((k) => (
                            <div
                                key={k.id}
                                className="flex items-center justify-between rounded-lg border bg-white px-4 py-3"
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-slate-900">{k.client_name}</p>
                                    {k.description && (
                                        <p className="truncate text-xs text-slate-500">{k.description}</p>
                                    )}
                                    <p className="mt-0.5 font-mono text-xs text-slate-400">{k.client_id}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-slate-400">
                                        {new Date(k.created_at).toLocaleDateString()}
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-red-500 hover:bg-red-50 hover:text-red-700"
                                        onClick={() => setRevokeId(k.id)}
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Key Sheet */}
            <Sheet open={createOpen} onOpenChange={(open) => !open && closeCreateSheet()}>
                <SheetContent className="sm:max-w-lg">
                    <SheetHeader>
                        <SheetTitle>
                            {credentials ? 'Key Created' : 'Create API Key'}
                        </SheetTitle>
                        <SheetDescription>
                            {credentials
                                ? 'Copy credentials now — the secret cannot be retrieved later.'
                                : 'Create a client credential for calling capabilities of this application.'}
                        </SheetDescription>
                    </SheetHeader>

                    {!credentials ? (
                        <div className="space-y-4 p-4">
                            <div className="space-y-2">
                                <Label htmlFor="key-name">Display name</Label>
                                <Input
                                    id="key-name"
                                    placeholder="e.g. Production Backend"
                                    value={createName}
                                    onChange={(e) => setCreateName(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="key-desc">Description (optional)</Label>
                                <Input
                                    id="key-desc"
                                    placeholder="What this key is used for..."
                                    value={createDesc}
                                    onChange={(e) => setCreateDesc(e.target.value)}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 p-4">
                            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                                <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
                                <p className="text-sm text-amber-800">
                                    Store the secret securely. This is the only time it will be shown.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label>Client ID</Label>
                                <div className="flex gap-2">
                                    <Input readOnly value={credentials.client_id} className="font-mono text-sm" />
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => copyToClipboard(credentials.client_id, 'id')}
                                    >
                                        {copiedId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Client Secret</Label>
                                <div className="flex gap-2">
                                    <Input readOnly value={credentials.client_secret} className="font-mono text-sm" />
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => copyToClipboard(credentials.client_secret, 'secret')}
                                    >
                                        {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Use with: MZ_CLIENT_ID and MZ_CLIENT_SECRET environment variables.
                            </p>
                        </div>
                    )}

                    <SheetFooter className="px-4 pb-4">
                        {!credentials ? (
                            <Button
                                onClick={handleCreate}
                                disabled={creating || !createName.trim()}
                                className="w-full gap-2"
                            >
                                {creating ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Key size={16} />
                                )}
                                {creating ? 'Creating...' : 'Create Key'}
                            </Button>
                        ) : (
                            <Button onClick={closeCreateSheet} className="w-full">
                                Done
                            </Button>
                        )}
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            {/* Revoke Confirmation */}
            <AlertDialog open={!!revokeId} onOpenChange={(open) => !open && setRevokeId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will immediately invalidate the key. Any applications using it will stop working.
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleRevoke}
                            disabled={revoking}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {revoking ? 'Revoking...' : 'Revoke'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
